"use client";

import { type BlokProps, getMedia, StoryblokComponentList } from "@trydig/storyblok";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { Breadcrumbs, BreadcrumbsSkeleton } from "@/components/ui/Breadcrumbs";
import { useHideBreadcrumbs } from "@/components/ui/Breadcrumbs/visibility";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { FullWidthMedia } from "@/components/ui/FullWidthMedia";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tooltip } from "@/components/ui/Tooltip";
import { Typography } from "@/components/ui/Typography";
import { ArticleShareTooltip } from "@/features/articles/ArticleListingPage/components/ArticleShareTooltip";
import type { ArticlePageStoryblok } from "@/generated/sb/sb-types";
import { useTranslations } from "@/i18n/client";
import { capitalizeFirstLetter } from "@/lib/utils/capitalizeFirstLetter";
import { getBaseUrl } from "@/lib/utils/getBaseUrl";

export const ArticlePageBlok = ({
	blok,
	preview,
	slug,
}: BlokProps<ArticlePageStoryblok> & {
	slug?: string[];
}) => {
	const t = useTranslations();
	const hideBreadcrumbs = useHideBreadcrumbs();

	const media = blok.asset ? getMedia(blok.asset) : null;

	return (
		<BlokWrap editable={blok} as="main" className="bg-background-bg-sand-1">
			<Container>
				<header
					className={
						"layout-grid py-grid-section-horizontal-margin-page-base lg:py-8 gap-y-grid-section-vertical-md!"
					}
				>
					{!hideBreadcrumbs && (
						<Breadcrumbs
							className="col-start-1!"
							items={(slug || []).map((label, i) => {
								const isLast = i === (slug || []).length - 1;
								const href = isLast ? undefined : `/${(slug || []).slice(0, i + 1).join("/")}`;

								return {
									label: capitalizeFirstLetter(label).replaceAll("-", " "),
									href,
								};
							})}
						/>
					)}
					<div className="flex justify-between items-center col-start-1! -col-end-1!">
						<Typography as="h1" variant="heading-display-sm" className=" md:text-5xl">
							{blok.title}
						</Typography>
						<Tooltip
							tip={{
								header: t("share"),
								content: <ArticleShareTooltip url={`${getBaseUrl()}/${slug?.join("/")}`} />,
							}}
							trigger="click"
						>
							<Button
								className="hidden sm:block uppercase"
								variant="outline"
								color="primary"
								size="medium"
							>
								{t("share")}
							</Button>
						</Tooltip>
					</div>
					<Typography
						as="h2"
						variant="body-sm-reg"
						className="col-span-full md:col-start-1! md:col-span-8!"
					>
						{blok.description}
					</Typography>
					<Tooltip
						tip={{
							header: t("share"),
							content: <ArticleShareTooltip url={`${getBaseUrl()}/${slug?.join("/")}`} />,
						}}
						trigger="click"
					>
						<Button
							className="block col-start-1! -col-end-1! sm:hidden uppercase"
							variant="outline"
							color="primary"
							size="medium"
						>
							{t("Share")}
						</Button>
					</Tooltip>
				</header>
			</Container>

			{media && <FullWidthMedia asset={media} label={blok.asset?.name} />}
			{blok.body && blok.body.length > 0 && (
				<StoryblokComponentList bloks={blok.body} preview={preview} />
			)}
		</BlokWrap>
	);
};

export const ArticlePageBlokSkeleton = ({
	blok,
	slug,
}: BlokProps<ArticlePageStoryblok> & {
	slug?: string[];
}) => {
	const t = useTranslations();
	const hideBreadcrumbs = useHideBreadcrumbs();

	return (
		<BlokWrap editable={blok} as="main" className="bg-background-bg-sand-1">
			<header
				className={
					"layout-grid py-grid-section-horizontal-margin-page-base lg:py-8 gap-y-grid-section-vertical-md!"
				}
			>
				{!hideBreadcrumbs && <BreadcrumbsSkeleton length={slug?.length || 3} />}
				<div className="flex justify-between items-center">
					<Skeleton fitContent>
						<Typography as="h1" variant="heading-display-sm" className="md:text-5xl">
							{blok.title}
						</Typography>
					</Skeleton>
					<Skeleton fitContent className="hidden sm:block">
						<Button className="uppercase" variant="outline" color="primary" size="medium">
							{t("share")}
						</Button>
					</Skeleton>
				</div>
				<Skeleton fitContent>
					<Typography
						as="h2"
						variant="body-sm-reg"
						className="col-span-full md:col-start-2! md:col-span-8!"
					>
						{blok.description}
					</Typography>
				</Skeleton>
				<Skeleton fitContent className="block sm:hidden">
					<Button className="uppercase" variant="outline" color="primary" size="medium">
						{t("share")}
					</Button>
				</Skeleton>
			</header>
		</BlokWrap>
	);
};
